import React, { useEffect, useRef } from "react";
import { Modal } from "antd";
import { fireConfettiAt, prefersReducedMotion } from "./questReward";

interface LevelUpCelebrationModalProps {
  open: boolean;
  fromLevel: number;
  toLevel: number;
  onClose: () => void;
}

// Số level mới pop vào sau khi thanh EXP (1.6s) gần chạy đầy.
const POP_DELAY_MS = 1500;

const LevelUpCelebrationModal = ({
  open,
  fromLevel,
  toLevel,
  onClose,
}: LevelUpCelebrationModalProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const reducedMotion = prefersReducedMotion();
  const levelsGained = toLevel - fromLevel;

  // Bắn confetti khớp thời điểm số level pop; bỏ qua khi reduced-motion.
  useEffect(() => {
    if (!open || reducedMotion) return;
    const timer = window.setTimeout(() => {
      fireConfettiAt(contentRef.current);
    }, POP_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [open, reducedMotion]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={560}
      styles={{ content: { padding: 0, borderRadius: 24, overflow: "hidden" } }}
    >
      <div
        ref={contentRef}
        className="flex flex-col items-center gap-[28px] rounded-[24px] border-2 border-[#002959] bg-[linear-gradient(155deg,#1b4b86_0%,#002959_100%)] px-[32px] py-[40px] text-center"
      >
        <div className="font-['Audiowide'] text-[26px] leading-none text-[#d4f754] [text-shadow:0_2px_8px_rgba(0,0,0,0.35)] sm:text-[30px]">
          🎉 LEVEL UP！
        </div>

        {/* Số level: cũ → mới (mới pop vào sau khi bar chạy) */}
        <div className="flex items-center justify-center gap-[18px]">
          <span className="font-['Audiowide'] text-[36px] leading-none text-white/40 line-through sm:text-[40px]">
            Lv.{fromLevel}
          </span>
          <span className="font-['Noto_Sans_JP'] text-[24px] leading-none text-white/70">
            →
          </span>
          <span
            className={`font-['Audiowide'] text-[56px] leading-none text-[#d4f754] [text-shadow:0_2px_12px_rgba(212,247,84,0.5)] sm:text-[64px] ${
              reducedMotion ? "" : "animate-level-pop"
            }`}
            style={
              reducedMotion
                ? undefined
                : { animationDelay: `${POP_DELAY_MS}ms` }
            }
          >
            Lv.{toLevel}
          </span>
        </div>

        {/* Thanh EXP trang trí: chạy đầy 0 → 100% */}
        <div className="h-[16px] w-full overflow-hidden rounded-[8px] bg-[#002959]">
          <div
            className={`h-full w-full rounded-[8px] bg-[linear-gradient(90deg,#d4f754_0%,#2cdcec_75%,#ff4a84_100%)] ${
              reducedMotion ? "" : "origin-left animate-exp-fill"
            }`}
          />
        </div>

        <div className="font-['Noto_Sans_JP'] text-[15px] leading-relaxed text-white sm:text-[16px]">
          構造レベル Lv.{toLevel} を達成しました！
          {levelsGained > 1 && (
            <span className="ml-[10px] inline-block rounded-full bg-[#d4f754] px-[12px] py-[3px] font-['Audiowide'] text-[14px] leading-none text-[#1b4b86]">
              +{levelsGained} UP
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={onClose}
          className="min-h-[48px] w-[240px] rounded-[6px] bg-[#d4f754] px-[20px] font-['Noto_Sans_JP'] text-[16px] font-bold leading-none text-[#1b4b86]"
        >
          OK
        </button>
      </div>
    </Modal>
  );
};

export default LevelUpCelebrationModal;
